import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApiQuery } from '../../../utils/api';
import { Loading, Error } from '../../Elements/Indicators';
import { PageHeader } from '../../Elements/PageHeader';
import { RadioGroup } from '@headlessui/react';
import { UsersIcon, TrophyIcon, BugAntIcon, FireIcon, CurrencyDollarIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import {
  ProjectLeaderboardResponse,
  OverallLeaderboardResponse,
  LeaderboardEntry,
  XProject
} from '@jirax/common';

// Interface for route parameters
interface RouteParams extends Record<string, string | undefined> {
  boardId?: string;
}

// Interface for board data in the selector
interface BoardData {
  projectName: string;
  projectKey: string;
  projectId: number;
  id: number;
  name: string;
}

// Interface for project data
interface ProjectData extends XProject {
  boards: {
    id: number;
    name: string;
  }[];
}

// Helper function to extract boards from projects
const getBoards = (projects: ProjectData[]): BoardData[] => {
  const boards = projects.flatMap(project =>
    project.boards.map(board => ({
      projectName: project.name,
      projectKey: project.key,
      projectId: Number(project.id),
      id: board.id,
      name: board.name
    }))
  );
  
  // Add "Overall" as the first option
  boards.unshift({
    projectName: 'Overall',
    projectKey: 'overall',
    projectId: 0,
    id: 0,
    name: 'Overall'
  });
  
  return boards;
};

// Helper function to get the first board for initial navigation
const getFirstBoard = (projects: ProjectData[]): BoardData => {
  projects.sort((a, b) => (a.id > b.id ? -1 : 1));
  const boards = getBoards(projects);
  return boards[0]; // Return Overall (first item)
};

// Helper to convert a count into a **static** Tailwind grid-cols class
const getGridCols = (count: number): string => {
  // For a single board we rely on the base `grid-cols-1` class already
  // present on the wrapper (no extra responsive class needed).
  if (count <= 1) return '';

  // 2 boards should still render as a single column on small screens
  // while leaving space to expand later, so we keep sm:grid-cols-1.
  if (count <= 2) return 'sm:grid-cols-1';
  if (count <= 3) return 'sm:grid-cols-3';
  if (count <= 4) return 'sm:grid-cols-4';
  if (count <= 5) return 'sm:grid-cols-5';
  return 'sm:grid-cols-6';
};

// Component to display a single row in the leaderboard
const LeaderboardRow: React.FC<{ entry: LeaderboardEntry; isEven: boolean }> = ({ entry, isEven }) => {
  return (
    <tr className={isEven ? 'bg-gray-50' : 'bg-white'}>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            {entry.avatar ? (
              <img className="h-10 w-10 rounded-full" src={entry.avatar} alt={entry.userName} />
            ) : (
              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                <span className="text-gray-600 text-sm font-medium">
                  {entry.userName.substring(0, 2).toUpperCase()}
                </span>
              </div>
            )}
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900 flex items-center">
              {entry.userName}
              {entry.rank === 1 && (
                <TrophyIcon className="h-5 w-5 ml-1 text-yellow-500" aria-hidden="true" />
              )}
              {/* 🔥 Most delivered points */}
              {entry.isTopPoints && (
                <FireIcon className="h-5 w-5 ml-1 text-red-500" aria-hidden="true" />
              )}
              {entry.isBugKing && (
                <BugAntIcon className="h-5 w-5 ml-1 text-purple-500" aria-hidden="true" />
              )}
            </div>
            {entry.role && (
              <div className="text-sm text-gray-500">{entry.role}</div>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <CurrencyDollarIcon className="h-5 w-5 text-yellow-500 mr-1" />
          <span className="text-sm font-medium">{entry.totalCoins}</span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">{entry.totalResolvedPoints} / {entry.totalStartPoints}</div>
        <div className="text-sm text-gray-500">{entry.deliveryRate}%</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">{entry.averagePointsPerSprint}</div>
        <div className="text-sm text-gray-500">{entry.totalSprints} sprints</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">{entry.totalResolvedBugs} / {entry.totalStartBugs}</div>
        <div className="text-sm text-gray-500">{entry.bugResolutionRate}%</div>
      </td>
    </tr>
  );
};

// Component for displaying the leaderboard table
const LeaderboardTable: React.FC<{
  entries: LeaderboardEntry[];
  isOverall: boolean;
}> = ({ entries, isOverall: _isOverall }) => {
  return (
    <div className="flex flex-col">
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Coins
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Delivered Points
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Velocity
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bugs Resolved
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                      No data available for this board.
                    </td>
                  </tr>
                ) : (
                  entries.map((entry, index) => (
                    <LeaderboardRow
                      key={`${entry.userId || 'uid'}-${index}`}
                      entry={entry}
                      isEven={index % 2 === 0}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main leaderboard component
const LeaderboardNew: React.FC = () => {
  const navigate = useNavigate();
  const { boardId } = useParams<RouteParams>();
  const [selectedBoardId, setSelectedBoardId] = useState<string>(boardId || '0');
  const isOverall = selectedBoardId === '0';

  // Fetch projects for the board selector
  const {
    isLoading: isLoadingProjects,
    error: projectsError,
    data: projects
  } = useApiQuery<ProjectData[]>(['projects']);

  // Fetch leaderboard data based on selected board
  const {
    isLoading: isLoadingLeaderboard,
    error: leaderboardError,
    data: leaderboardData
  } = useApiQuery<ProjectLeaderboardResponse | OverallLeaderboardResponse>(
    isOverall 
      ? ['leaderboards', 'overall'] 
      : ['leaderboards', 'projects', selectedBoardId],
    { enabled: !!selectedBoardId }
  );

  // Navigate to the first board if no boardId is specified
  useEffect(() => {
    if (projects && !boardId) {
      const firstBoard = getFirstBoard(projects);
      navigate(`/leaderboard/${firstBoard.id}`);
    }
  }, [projects, boardId, navigate]);

  // Update selected board when URL param changes
  useEffect(() => {
    if (boardId) {
      setSelectedBoardId(boardId);
    }
  }, [boardId]);

  // Handle board selection change
  const handleBoardChange = (newBoardId: string) => {
    navigate(`/leaderboard/${newBoardId}`);
  };

  // Show loading state when fetching projects
  if (isLoadingProjects) {
    return <Loading message="Loading projects..." />;
  }

  // Show error state if projects fetch fails
  if (projectsError) {
    return <Error message={(projectsError as Error).message} />;
  }

  // Render the leaderboard UI
  return (
    <>
      <PageHeader config={{ pageTitle: 'Team Leaderboard' }} />
      <main>
        {/* Board selector */}
        {projects && (
          <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
            <RadioGroup
              value={selectedBoardId}
              onChange={handleBoardChange}
              className={clsx(
                'grid grid-cols-1 gap-2 px-4 sm:gap-8 sm:px-0',
                getGridCols(getBoards(projects).length)
              )}
            >
              {getBoards(projects).map((board) => (
                <RadioGroup.Option
                  key={`${board.projectKey}-${board.id}`}
                  value={String(board.id)}
                  className={({ checked }) =>
                    clsx(
                      checked && 'bg-sky-900 bg-opacity-75 text-white',
                      // base card look with subtle shadow
                      'relative col-span-1 flex cursor-pointer rounded-lg px-5 py-2 shadow-md',
                      // hover & transition states
                      'transition-all duration-150 hover:shadow-lg hover:ring-2 hover:ring-indigo-500/50',
                      // stronger shadow on focus/active (replacing blue outline)
                      'focus:outline-none focus-visible:shadow-lg active:shadow-lg'
                    )
                  }
                >
                  {({ checked }) => (
                    <>
                      <div className="flex w-full items-center justify-between">
                        <div className="mr-4 shrink-0 text-white">
                          {checked ? (
                            <UsersIcon className="text-white-700 h-6 w-6" />
                          ) : (
                            <UsersIcon className="h-6 w-6 text-slate-500" />
                          )}
                        </div>

                        <div className="flex grow items-center">
                          <div className="text-sm">
                            <RadioGroup.Label
                              as="p"
                              className={`font-medium ${
                                checked ? 'text-white' : 'text-gray-900'
                              }`}
                            >
                              {board.projectName}
                            </RadioGroup.Label>
                            <RadioGroup.Description
                              as="span"
                              className={`inline ${
                                checked ? 'text-sky-100' : 'text-gray-500'
                              }`}
                            >
                              <span>{board.name}</span>
                            </RadioGroup.Description>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </RadioGroup.Option>
              ))}
            </RadioGroup>
          </div>
        )}

        {/* Leaderboard content */}
        <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="px-4 py-8 sm:px-0">
            {isLoadingLeaderboard ? (
              <Loading message="Loading leaderboard data..." />
            ) : leaderboardError ? (
              <Error message={(leaderboardError as Error).message} />
            ) : leaderboardData ? (
              <>
                {/* Leaderboard header with metadata */}
                <div className="mb-6">
                  <h2 className="text-lg font-medium text-gray-900">
                    {isOverall
                      ? `Overall Leaderboard (${(leaderboardData as OverallLeaderboardResponse).totalProjects} Projects)`
                      : `${(leaderboardData as ProjectLeaderboardResponse).projectName} - ${(leaderboardData as ProjectLeaderboardResponse).boardName}`}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {isOverall
                      ? `Showing data from ${(leaderboardData as OverallLeaderboardResponse).totalSprints} sprints across ${(leaderboardData as OverallLeaderboardResponse).totalBoards} boards`
                      : `Showing data from ${(leaderboardData as ProjectLeaderboardResponse).totalSprints} sprints`}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Last updated: {new Date(leaderboardData.lastUpdated).toLocaleString()}
                  </p>
                </div>

                {/* Legend for badges */}
                <div className="mb-4 flex flex-wrap gap-4">
                  <div className="flex items-center">
                    <TrophyIcon className="h-5 w-5 text-yellow-500 mr-1" />
                    <span className="text-sm text-gray-600">Top Rank</span>
                  </div>
                  <div className="flex items-center">
                    <FireIcon className="h-5 w-5 text-red-500 mr-1" />
                    <span className="text-sm text-gray-600">Top Points</span>
                  </div>
                  <div className="flex items-center">
                    <BugAntIcon className="h-5 w-5 text-purple-500 mr-1" />
                    <span className="text-sm text-gray-600">Bug King/Queen</span>
                  </div>
                </div>

                {/* Leaderboard table */}
                <LeaderboardTable
                  entries={leaderboardData.entries}
                  isOverall={isOverall}
                />
              </>
            ) : (
              <div className="text-center py-10">
                <p className="text-gray-500">Select a board to view the leaderboard</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default LeaderboardNew;
